import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  B2bDataService,
  SFSOptions,
} from '@nimmsta/ng-commons-lib/data-access';
import { DeviceSoftwareStatus, Pageable } from '@nimmsta/ng-commons-lib/models';
import { FirmwareBundleLatestVersionService } from '@nimmsta/ng-commons-lib/services/firmware-bundle-latest-version';
import dayjs from 'dayjs';
import { Scanner, SimpleScanner } from '../models/scanner';
import { ScannerConnection } from '../models/scannerConnection';
import { StatusCount } from '../models/overviewData';
import { map, tap } from 'rxjs/operators';

export function resolveScannerSoftwareStatus(
  scanner: SimpleScanner,
  latestVersion?: string,
): DeviceSoftwareStatus {
  if (scanner.firmwareUpdateRequested) {
    return DeviceSoftwareStatus.UPDATE_REQUESTED;
  }

  if (scanner.firmwareBundleIdentified === false) {
    return DeviceSoftwareStatus.CUSTOM_FIRMWARE;
  }

  if (!scanner.firmwareBundleVersion || !latestVersion) {
    return DeviceSoftwareStatus.MISSING_DATA;
  }

  if (scanner.firmwareBundleVersion !== latestVersion) {
    return DeviceSoftwareStatus.WARNING;
  }

  if (scanner.imagerFirmwareUpdateRequired === true) {
    return DeviceSoftwareStatus.IMAGER_FIRMWARE_UPDATE_REQUIRED;
  }

  return DeviceSoftwareStatus.OK;
}

@Injectable({
  providedIn: 'root',
})
export class ScannerService extends B2bDataService<Scanner> {
  private firmwareBundleLatestVersionService = inject(
    FirmwareBundleLatestVersionService,
  );

  protected override get apiEndpoint(): string {
    return '/smartwatches';
  }

  listItems(
    options?: Partial<SFSOptions>,
  ): Observable<Pageable<SimpleScanner>> {
    return this.http
      .get<Pageable<SimpleScanner>>(this.baseUrl + this.apiEndpoint, {
        params: this.getParams(options ?? {}).set('includeChildren', true),
      })
      .pipe(
        map((page) => {
          page.content = page.content.map((item) =>
            this.loadedSimpleDataTransform(item),
          );
          return page;
        }),
      );
  }

  getConnections(scannerId: string): Observable<Pageable<ScannerConnection>> {
    return this.http.get<Pageable<ScannerConnection>>(
      `${this.baseUrl + this.apiEndpoint}/${scannerId}/connections?page=0&size=10&includeChildren=true`,
    );
  }

  override get(id: string): Observable<Scanner> {
    return this.http
      .get<Scanner>(
        `${this.baseUrl + this.apiEndpoint}/${id}?includeChildren=true`,
      )
      .pipe(map((item) => this.loadedDataTransform(item)));
  }

  getCounts(): Observable<StatusCount[]> {
    return this.http.get<StatusCount[]>(
      `${this.baseUrl + this.apiEndpoint}/count`,
    );
  }

  protected override loadedDataTransform(data: Scanner): Scanner {
    const scanner = super.loadedDataTransform(data);
    this.applySimpleDataTransform(scanner);
    return scanner;
  }

  private loadedSimpleDataTransform<T extends SimpleScanner>(data: T): T {
    const scanner = data;
    scanner.createdAt = scanner.createdAt
      ? dayjs(scanner.createdAt)
      : scanner.createdAt;
    scanner.lastUpdatedAt = scanner.lastUpdatedAt
      ? dayjs(scanner.lastUpdatedAt)
      : scanner.lastUpdatedAt;
    this.applySimpleDataTransform(scanner);
    return scanner;
  }

  private applySimpleDataTransform(scanner: SimpleScanner): void {
    if (scanner.warrantyBaseEndDate) {
      scanner.warrantyBaseEndDate = dayjs(scanner.warrantyBaseEndDate);
    }
    if (scanner.warrantyFullEndDate) {
      scanner.warrantyFullEndDate = dayjs(scanner.warrantyFullEndDate);
    }

    if (scanner.careArticles) {
      scanner.careArticles = scanner.careArticles.map((article) => {
        const newArticle = { ...article };
        if (newArticle.purchasedDate) {
          newArticle.purchasedDate = dayjs(article.purchasedDate);
        }
        return newArticle;
      });
    }

    this.updateListStatusSummary(scanner);
  }

  resolveSoftwareStatus(scanner: SimpleScanner): DeviceSoftwareStatus {
    return resolveScannerSoftwareStatus(
      scanner,
      scanner.firmwareBundleLatestVersion,
    );
  }

  resolveSoftwareStatuses(
    scanners: SimpleScanner[],
  ): Observable<string | undefined> {
    // Rollout controls are hidden; do not call the manager-only rollout API yet.
    return this.firmwareBundleLatestVersionService.getLatestVersion().pipe(
      tap((latestVersion) => {
        scanners.forEach((scanner) => {
          scanner.firmwareUpdateRequested = false;
          scanner.firmwareBundleLatestVersion = latestVersion;
          scanner.softwareStatus = resolveScannerSoftwareStatus(
            scanner,
            latestVersion,
          );
          this.updateListStatusSummary(scanner);
        });
      }),
    );
  }

  private updateListStatusSummary(scanner: SimpleScanner): void {
    scanner.listStatusSummary = JSON.stringify({
      maintenanceStatus: scanner.maintenanceStatus ?? null,
      softwareStatus: scanner.softwareStatus ?? null,
    });
  }
}
